/* Housing picker — one component for every listing shaped "one product, a variant per housing".
 *
 * Serves the zoom/focus gears and the O-ring sets, on a product page (replacing the theme's variant
 * selector) and on a finder page (where there is no product form, so it grows its own Add to cart).
 * The copy comes from the document's `t` (the kind, e.g. "Zoom Gear" / "O-Ring Set") and optional `l`
 * (the subject, e.g. the lens), so a third family needs a document, not a third picker.
 *
 * On a product page it replaces the theme's variant selector. A gear listing carries a variant per housing — up to 27 —
 * and the shopper does not think in variants, they think "I have an A7 IV". So this asks for the
 * camera brand and the housing, then RESOLVES the variant itself and writes it into the product
 * form's hidden `id` input, which is what the theme's own Add-to-cart submits. Nothing about the
 * cart flow is bypassed.
 *
 * Resolution comes from assets/dvl-gear.json, built from the EQA catalog: `catn_zoom_gears` holds one
 * row per SKU, each scoped to its own housings via `catn_zg_listings`, so a lens needing several cuts
 * (the Fujifilm XC 16-50 needs three; the Sony E 10-18 needs an 80mm one for the A6XXX and a 90mm one
 * for the rest) maps each housing to the right SKU. That is a catalog fact and cannot be derived from
 * Shopify, where all 27 variants may share one SKU.
 *
 * Prices and stock are NOT in that file — they come from /products/<handle>.js, so they are live and
 * in the shopper's market currency.
 */
(() => {
  const money = (cents, fmt) => {
    if (cents == null) return "";
    const n = (cents / 100).toFixed(2);
    // the theme's money_format, e.g. "${{amount}}" — fall back to a bare amount
    return fmt ? fmt.replace(/\{\{\s*amount[^}]*\}\}/, n) : n;
  };

  // `shop.money_format` is a theme SETTING, and on some themes it is markup rather than text -
  // Fashionopolism ships "<span class=money>${{amount}} USD</span>" so currency-converter apps can find
  // the price. Through textContent those tags render literally, so a price is the one thing this file
  // writes as HTML: the format is the shop's, the amount is our own digits.
  const setMoney = (node, cents, fmt) => { node.innerHTML = money(cents, fmt); };

  /* One line into Shopify's own analytics bus: the custom pixel subscribes to `all_events` and
     forwards the dvl_* ones to EQA's beacon (see docs/analytics.md). This file therefore has no
     endpoint, no consent handling and no idea where the data goes. With no pixel installed
     `Shopify.analytics` is absent and this is a no-op — and it is wrapped because a storefront must
     never break for a measurement. */
  const track = (name, data) => {
    try {
      // Two transports on purpose. `Shopify.analytics.publish` is for the custom pixel, which is the
      // intended route and the only one that reaches checkout; `EqaBeacon` is the theme-side beacon
      // that carries these today, because a custom pixel cannot be installed by API. With both
      // present the pixel forwards nothing of ours twice — it subscribes to the bus, and EqaBeacon
      // posts directly — but page views WOULD double, which is why installing the pixel means
      // removing the beacon from layout/theme.liquid.
      const a = window.Shopify && window.Shopify.analytics;
      if (a && typeof a.publish === "function") a.publish(name, data || {});
      if (window.EqaBeacon) window.EqaBeacon.send(name, data || {});
    } catch (e) {
      /* never a reason for a page to fail */
    }
  };

  const el = (tag, cls, text) => {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
  };

  const CART_ADD = /\/cart\/add(\.js)?(\?|$)/;
  // the picker that is currently refusing purchases, if any
  let cartGuard = null;

  /** Every variant id in a cart-add payload, whatever shape the caller used. */
  const idsInBody = (body) => {
    const out = [];
    const push = (x) => { if (x != null && x !== "") out.push(String(x)); };
    const fromItems = (o) => {
      const items = Array.isArray(o) ? o : (o && o.items) || [o];
      for (const it of items) push(it && it.id);
    };
    try {
      if (body == null) return out;
      if (typeof body === "string") {
        const t = body.trim();
        if (t.startsWith("{") || t.startsWith("[")) fromItems(JSON.parse(t));
        else {
          const p = new URLSearchParams(t);
          p.getAll("id").forEach(push);
          p.forEach((v, k) => { if (/^items\[\d+\]\[id\]$/.test(k)) push(v); });
        }
      } else if (typeof FormData !== "undefined" && body instanceof FormData) {
        body.getAll("id").forEach(push);
        body.forEach((v, k) => { if (/^items\[\d+\]\[id\]$/.test(k)) push(v); });
      } else if (typeof body === "object") {
        fromItems(body);
      }
    } catch (e) {
      /* a payload we cannot read is not a reason to let it through — it is caught by the empty case */
    }
    return out;
  };

  /** The last line of the gate.
   *
   *  While no housing is chosen, a cart add FOR THIS PRODUCT is answered the way Shopify answers an
   *  impossible one — 422 with a reason — and the picker says what to do. This is what makes the
   *  choice required rather than merely encouraged: it holds for the options app's own drawer, for a
   *  second Add to cart this script never saw, and for anything else on the page that posts a cart
   *  add. A request naming another product passes through untouched; one naming no variant at all is
   *  ours, because that is the theme's placeholder — exactly the add that must not happen.
   *  Installed once per page, and it stops refusing the moment a housing is chosen. */
  const installCartGuard = () => {
    if (window.__dvlGearCartGuard) return;
    window.__dvlGearCartGuard = true;
    const refuse = () => {
      if (cartGuard) cartGuard.nag();
      const payload = {
        status: 422,
        message: "Choose your underwater housing",
        description: "Choose the housing you own first — this part is made for one housing.",
      };
      // an engine without `Response` still gets something a caller can read, rather than a throw from
      // inside the guard — which would be a worse failure than the one it is here to prevent
      if (typeof Response === "function") {
        return new Response(JSON.stringify(payload), {
          status: 422,
          statusText: "Unprocessable Entity",
          headers: { "Content-Type": "application/json" },
        });
      }
      return {
        ok: false,
        status: 422,
        statusText: "Unprocessable Entity",
        json: () => Promise.resolve(payload),
        text: () => Promise.resolve(JSON.stringify(payload)),
      };
    };
    const nativeFetch = typeof window.fetch === "function" && window.fetch.bind(window);
    if (nativeFetch) {
      window.fetch = function (input, init) {
        try {
          const url = String((input && input.url) || input || "");
          const method = String((init && init.method) || (input && input.method) || "GET").toUpperCase();
          if (method === "POST" && CART_ADD.test(url) && cartGuard &&
              cartGuard.blocksCartAdd(init && init.body)) {
            return Promise.resolve(refuse());
          }
        } catch (e) {
          /* the guard must never be the reason an unrelated request fails */
        }
        return nativeFetch(input, init);
      };
    }
    const X = window.XMLHttpRequest && window.XMLHttpRequest.prototype;
    if (X && X.open && X.send) {
      const open = X.open;
      const send = X.send;
      X.open = function (m, u) {
        this.__dvl = { m: String(m || "").toUpperCase(), u: String(u || "") };
        return open.apply(this, arguments);
      };
      X.send = function (body) {
        const t = this.__dvl;
        if (t && t.m === "POST" && CART_ADD.test(t.u) && cartGuard && cartGuard.blocksCartAdd(body)) {
          cartGuard.nag();
          this.abort(); // the caller sees a failed request, which is what it is
          return;
        }
        return send.apply(this, arguments);
      };
    }
  };

  // the order the catalog and the finders use, so every surface leads with Sony
  const BRAND_ORDER = ["Sony", "Canon", "Fujifilm", "Olympus", "Panasonic", "Nikon"];
  const byOrder = (list) => {
    const rank = (b) => {
      const i = BRAND_ORDER.indexOf(b);
      return i === -1 ? BRAND_ORDER.length : i;
    };
    return [...list].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
  };

  class GearPicker {
    constructor(root) {
      this.root = root;
      this.handle = root.dataset.handle;
      this.rootUrl = (root.dataset.root || "/").replace(/\/+$/, "");
      this.fmt = root.dataset.moneyFormat || "";
      this.form = null;
      this.gear = null;
      this.live = null;
      this.brand = null;
      this.init();
    }

    async init() {
      /* The form that HOLDS the picker is the one being submitted. Taking the first cart form in the
         document instead put the chosen variant into Shopify's payment-terms form on Fashionopolism
         (`product-form-installment` precedes `AddToCartForm`), and the theme went on submitting its
         default variant - the wrong gear, silently. A cart form with a submit button is the next best
         guess, because the payment-terms form has none. */
      const carts = [...document.querySelectorAll('form[action*="/cart/add"]')];
      this.form =
        this.root.closest('form[action*="/cart/add"]') ||
        carts.find((f) => f.querySelector('button[name="add"], input[name="add"], button[type="submit"]')) ||
        carts[0] ||
        document.querySelector("product-form form");
      try {
        const [doc, live] = await Promise.all([
          fetch(this.root.dataset.src, { headers: { Accept: "application/json" } }).then((r) => r.json()),
          fetch(`${this.root.dataset.root || "/"}products/${this.handle}.js`.replace(/\/{2,}/g, "/")).then((r) =>
            r.ok ? r.json() : null,
          ),
        ]);
        this.gear = (doc.gears || []).find((g) => g.h === this.handle) || null;
        this.imgs = doc.imgs || [];
        this.live = live;
      } catch (e) {
        this.fail("We could not load the housing list just now. Please reload the page.");
        return;
      }
      if (!this.gear) {
        this.fail("");
        return;
      }
      this.brands = byOrder(new Set(this.gear.o.map((o) => o.b)));
      // ?housing=<listing id> — the finder deep-links straight to the right housing
      const want = new URLSearchParams(location.search).get("housing");
      const pre = want ? this.matchListing(want) : null;
      // a link that named a housing and resolved to nothing is a dead deep link, and it is silent:
      // the shopper simply sees an unchosen picker and a placeholder SKU. Worth counting.
      if (want && !pre) track("dvl_deeplink_missed", { surface: "picker", housing: want });
      // default to the first brand in that order (Sony), so the housing list is usable on arrival
      // rather than behind a click — the same default the finders take
      this.brand = pre ? pre.b : this.brands[0] || null;
      this.render();
      if (pre) {
        this.combo.setValue(pre.h);
        // a shopper who arrived on a link that already names their housing skipped the step entirely;
        // worth knowing separately from one who chose it here, so the funnel is not flattered by it
        track("dvl_deeplink_landed", { surface: "picker", housing: pre.h });
        this.choose(pre.h, "deeplink"); // choose() locks onto the resolved variant itself
      } else {
        this.lock(); // nothing chosen yet — gate the theme's Add to cart
      }
    }

    /** With no data for this product, leave the page alone rather than showing a broken picker. */
    fail(msg) {
      this.root.hidden = !msg;
      if (msg) this.root.append(el("p", "dvl-g__empty", msg));
    }

    render() {
      const g = this.gear;
      this.root.textContent = "";

      const step = (n, label, field, done, required) => {
        const s = el("div", "dvl-g__step");
        s.dataset.done = done ? "1" : "0";
        const f = el("div", "dvl-g__field");
        f.append(field);
        const lab = el("span", "dvl-g__label", label);
        if (required) {
          // said in words, not with a bare asterisk: this is the one choice the page cannot make for
          // the shopper, because the gear that fits an A7IV does not fit an A6600
          lab.append(el("span", "dvl-g__req", "required"));
        }
        s.append(el("span", "dvl-g__num", n), lab, f);
        return s;
      };

      const steps = el("div", "dvl-g__steps");
      let n = 1;

      if (this.brands.length > 1) {
        const chips = el("div", "dvl-g__chips");
        for (const b of this.brands) {
          const c = el("button", "dvl-g__chip", b);
          c.type = "button";
          c.setAttribute("aria-pressed", String(b === this.brand));
          c.addEventListener("click", () => {
            this.brand = b;
            track("dvl_brand_chosen", { surface: "picker", brand: b });
            this.render();
          });
          chips.append(c);
        }
        steps.append(step(String(n++), "Camera brand", chips, this.brand));
      }

      // a searchable combobox rather than a <select>: a gear listing can offer 27 housings
      const opts = g.o.filter((o) => !this.brand || o.b === this.brand);
      this.combo = new window.DvlGearCombo({
        placeholder: "Search or choose your housing…",
        emptyText: "No housing matches",
        items: opts.map((o) => ({ value: o.h, label: o.n })),
        onChange: (v) => this.choose(v),
      });
      this.combo.disabled = !opts.length;
      this.combo.input.setAttribute("aria-required", "true");
      this.step2 = step(String(n), "Your underwater housing", this.combo.el, false, true);
      steps.append(this.step2);
      this.root.append(steps);

      this.answer = el("div", "dvl-g__answer");
      this.answer.hidden = true;
      this.root.append(this.answer);

      this.hint = el("p", "dvl-g__note", `Select your housing to see the ${this.gear.t.toLowerCase()} that fits it.`);
      this.hint.style.marginTop = "1em";
      this.root.append(this.hint);
    }

    /** The finder route's own Add to cart, living in the answer panel. Kept out of the DOM entirely on
     *  a product page, where the theme's button is the one that must work. */
    ownBtn(v) {
      if (!this.answer || this.answer.hidden) return null;
      if (!this.own) {
        this.own = el("button", "dvl-g__btn");
        this.own.type = "button";
        this.own.addEventListener("click", () => this.addOwn());
      }
      if (this.own.parentNode !== this.answer) this.answer.append(this.own);
      this.ownVariant = v || null;
      return this.own;
    }

    async addOwn() {
      const v = this.ownVariant;
      if (!v) return;
      const was = this.own.textContent;
      this.own.disabled = true;
      this.own.textContent = "Adding…";
      try {
        const r = await fetch(`${this.rootUrl}/cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: [{ id: Number(v.id), quantity: 1 }] }),
        });
        if (!r.ok) throw new Error(await r.text());
        this.own.textContent = "Added";
        const p = el("p", "dvl-g__toast");
        p.innerHTML = `Added ${this.gear.t} for your housing. <a href="${this.rootUrl}/cart">View cart</a>`;
        this.answer.append(p);
      } catch {
        this.own.textContent = was;
        this.own.disabled = false;
      }
    }


    /** An interned image reference -> its url. `i` is absent when a part has no image, and index 0 is a
     *  real reference, so this tests for a number rather than truthiness. */
    imgOf(o) {
      const i = o && o.i;
      return typeof i === "number" ? this.imgs[i] || "" : "";
    }

    /** The shot for one option.
     *
     *  The document already carries it, resolved by SKU with the collection composites excluded, so
     *  that is the first choice. The live product JSON is the fallback, and it applies the same two
     *  rules: a filename containing "collection" is the montage with the option circles — meant for a
     *  collection grid, never a part's portrait — and the SKU in the filename is what names the part
     *  (EQA publishes variant images that way and the themes scope galleries by it).
     *
     *  Deliberately no `featured_image` fallback: on these listings that IS often the collection
     *  composite, and showing the whole range on one cut's card is the bug this avoids. */
    imageFor(opt) {
      const interned = this.imgOf(opt);
      if (interned) return interned;
      const live = this.live;
      if (!live) return "";
      const sku = String((opt && opt.sku) || "").toLowerCase();
      const urls = (live.media || []).map((m) => m.src).concat(live.images || []).filter(Boolean);
      const clean = urls.filter((u) => !String(u).split("?")[0].split("/").pop().toLowerCase().includes("collection"));
      const named = sku && clean.find((u) => {
        const fn = String(u).split("?")[0].split("/").pop().toLowerCase();
        return [`${sku}-`, `${sku}.`, `${sku}_`].some((t) => fn.includes(t));
      });
      return named || clean[0] || "";
    }

    /** Which live variant is this housing?
     *
     *  Order matters. The baked variant id first, then the housing OPTION VALUE — never the SKU while
     *  the listing still has a variant per housing, because many of them share one SKU (10 of the 23
     *  O-ring SKUs cover 2-8 housings) and a SKU match would pick an arbitrary camera.
     *
     *  The SKU is the LAST resort, and it is there for the variant collapse: once a listing carries one
     *  variant per physical part instead of one per housing, the baked id and the housing option value
     *  both stop existing, and the SKU becomes unambiguous precisely because the collapse removed the
     *  duplicates. So this picker keeps working before and after that change. */
    variantFor(opt) {
      if (!this.live) return null;
      return (
        this.live.variants.find((v) => String(v.id) === String(opt.vid)) ||
        this.live.variants.find((v) => (v.options || []).includes(opt.ov)) ||
        this.live.variants.find((v) => (v.sku || "").trim() && v.sku.trim() === String(opt.sku || "").trim()) ||
        null
      );
    }

    /** The listing a `?housing=` names — exactly, or unambiguously.
     *
     *  The two data files disagree about what a listing id is. `dvl-gear.json` uses the short code the
     *  finders emit (`A6XXX`, `R5`); `dvl-oring.json` uses the full option label (`Salted Line CH
     *  A6XXX`). So the identical link works on a zoom gear page and does nothing on an o-ring page —
     *  no error, just an unchosen picker and the placeholder variant's SKU (`SFORLPNONE`) above it.
     *
     *  Matching the tail closes that, but only where it is safe: of 47 o-ring listings, five codes —
     *  A1, A9, A7IV, A7SIII, A6700 — exist under BOTH Sea Frogs and Salted Line, and those are
     *  different housings taking different seals. Two candidates is not a match, and guessing would
     *  sell somebody the wrong o-rings; those links are left for the shopper to resolve, as today. */
    matchListing(want) {
      const exact = this.gear.o.find((o) => o.h === want);
      if (exact) return exact;
      const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const w = norm(want);
      if (!w) return null;
      const near = this.gear.o.filter((o) => {
        const h = norm(o.h);
        return h === w || h.endsWith(w);
      });
      return near.length === 1 ? near[0] : null;
    }

    /** Keep ?housing= AND ?variant= in step with the choice, so a refresh or a shared product link
     *  keeps it and the shopper is not asked twice. The variant matters because Shopify renders the
     *  page from it: leave a stale one behind and a refresh serves the wrong housing's variant before
     *  the picker can correct it. Replace, never push: the product page is one step, and Back should
     *  take them out of it — usually to the finder they came from. */
    writeUrl(listingId) {
      const p = new URLSearchParams(location.search);
      const opt = listingId ? this.gear.o.find((o) => o.h === listingId) : null;
      if (listingId) p.set("housing", listingId);
      else p.delete("housing");
      if (opt && opt.vid) p.set("variant", opt.vid);
      else if (!listingId) p.delete("variant");
      const qs = p.toString();
      try {
        history.replaceState({ dvlGear: 1 }, "", `${location.pathname}${qs ? `?${qs}` : ""}${location.hash}`);
      } catch {
        /* a sandboxed frame can refuse history writes — the picker still works */
      }
    }

    choose(listingId, via) {
      const opt = this.gear.o.find((o) => o.h === listingId);
      this.step2.dataset.done = opt ? "1" : "0";
      // remembered so the URL can be re-asserted after a theme's own variant handler rewrites it
      this.chosenListing = opt ? listingId : null;
      this.writeUrl(opt ? listingId : null);
      if (!opt) {
        this.answer.hidden = true;
        this.hint.hidden = false;
        this.lock();
        return;
      }
      const v = this.variantFor(opt);
      const chosen = {
        surface: via === "deeplink" ? "picker-deeplink" : "picker",
        housing: opt.h,
        sku: opt.sku,
        lens: this.gear.l || "",
      };
      track("dvl_housing_chosen", chosen);
      // the housing they wanted, that we could not sell them — a stocking signal that exists nowhere
      // else, because Shopify only ever records what WAS bought
      if (!v || !v.available) track("dvl_sold_out_chosen", chosen);
      const sameCut = this.gear.o.filter((o) => o.sku === opt.sku);
      this.answer.textContent = "";

      const shot = this.imageFor(opt);
      this.answer.classList.toggle("dvl-g__answer--noshot", !shot);
      if (shot) {
        const img = el("img", "dvl-g__shot");
        img.src = `${shot}${shot.includes("?") ? "&" : "?"}width=320`;
        img.alt = this.gear.l ? `${this.gear.t} ${opt.sku} on the ${this.gear.l}` : `${this.gear.t} ${opt.sku}`;
        img.loading = "lazy";
        this.answer.append(img);
      }

      const head = el("div", "dvl-g__answer-head");
      // no subject beyond the housing (the O-rings) → the kind alone is the honest name
      head.append(el("span", "dvl-g__answer-name", this.gear.l ? `${this.gear.t} for ${this.gear.l}` : this.gear.t));
      if (v) {
        const p = el("span", "dvl-g__price");
        if (v.compare_at_price && v.compare_at_price > v.price) {
          const was = el("s");
          setMoney(was, v.compare_at_price, this.fmt);
          p.append(was);
        }
        const now = el("span");
        setMoney(now, v.price, this.fmt);
        p.append(now);
        head.append(p);
      }
      this.answer.append(head);

      const dl = el("dl", "dvl-g__facts");
      const fact = (k, val) => {
        if (!val) return;
        const d = document.createElement("div");
        d.append(el("dt", null, k), el("dd", null, val));
        dl.append(d);
      };
      // every spec here is the CUT's, not the listing's: the Sony E 10-18's A6XXX gear is an 80mm
      // rubber part while the other 21 housings take a 90mm polycarbonate one
      fact("Fits", opt.n);
      fact(this.gear.t, opt.sku);
      if (opt.by) fact("Bayonet", `${opt.by}mm`);
      if (opt.m) fact("Material", opt.m);
      if (opt.ms) fact("Mounting", opt.ms);
      this.answer.append(dl);

      // what is in the box, when the catalog says (the O-ring sets list their seals; gears have none)
      if (Array.isArray(opt.pkg) && opt.pkg.length) {
        this.answer.append(el("p", "dvl-g__note", `In the set: ${opt.pkg.join(", ")}.`));
      }

      // Worth saying out loud, for gears and O-rings alike: the right part changes across the housing
      // range, and this SKU may be shared with other housings — which is exactly why the page resolves
      // the variant rather than letting a SKU lookup pick one.
      if (this.gear.cuts > 1) {
        this.answer.append(
          el(
            "p",
            "dvl-g__note",
            `The right ${this.gear.t.toLowerCase()} changes across the housing range — ${opt.sku} is the one ` +
              `for your housing` +
              (sameCut.length > 1 ? ` and ${sameCut.length - 1} other${sameCut.length > 2 ? "s" : ""}.` : "."),
          ),
        );
      }
      if (v && !v.available) {
        this.answer.append(el("p", "dvl-g__note dvl-g__oos", "Out of stock for this housing."));
      }

      this.answer.hidden = false;
      this.hint.hidden = true;
      this.combo.input.removeAttribute("aria-invalid");
      delete this.step2.dataset.invalid;
      this.lock(v);
    }

    /** Point the theme's own product form at the resolved variant, and gate its Add to cart. With no
     *  form on the page — the finder route — the picker adds to the cart itself instead. */
    lock(v) {
      this.chosen = Boolean(v && v.available);
      if (this.form) {
        this.setFormVariant(v);
        cartGuard = this;
        installCartGuard();
      }
      /* EVERY Add to cart for this product, not the first one in the document: a theme may carry a
         second (sticky, or mobile) copy outside the form, and on seafrogs that copy reached the cart
         while the one beside the picker was still gated. Scoped to forms that buy THIS product, so a
         recommendation further down the page keeps its own button. */
      const btns = this.form ? this.addButtons() : [this.ownBtn(v)].filter(Boolean);
      if (!btns.length) return;
      const ok = this.chosen;
      const label = !v ? "Select your housing" : v.available ? "Add to cart" : "Sold out";
      for (const btn of btns) {
        btn.disabled = !ok;
        const span = btn.querySelector("span");
        if (span) span.textContent = label;
        else btn.textContent = label;
        this.gate(btn, ok);
      }
    }

    /** This listing's variant ids — the live product is the authority, the document the fallback. */
    variantIds() {
      const out = new Set();
      for (const v of (this.live && this.live.variants) || []) out.add(String(v.id));
      for (const o of (this.gear && this.gear.o) || []) if (o.vid) out.add(String(o.vid));
      return out;
    }

    /** The submit buttons of every form on the page that buys this product. */
    addButtons() {
      const ids = this.variantIds();
      const forms = [...document.querySelectorAll('form[action*="/cart/add"]')].filter((f) => {
        if (f === this.form) return true;
        const field = f.querySelector('[name="id"]');
        return Boolean(field && ids.has(String(field.value)));
      });
      if (!forms.includes(this.form)) forms.push(this.form);
      const out = [];
      for (const f of forms) {
        for (const b of f.querySelectorAll('button[name="add"], input[name="add"], button[type="submit"]')) {
          if (!out.includes(b)) out.push(b);
        }
      }
      return out;
    }

    /** Is this cart add one we must refuse? See installCartGuard. */
    blocksCartAdd(body) {
      if (this.chosen) return false;
      const ids = idsInBody(body);
      if (!ids.length) return true; // no variant named — the theme's placeholder
      const ours = this.variantIds();
      return ids.some((id) => ours.has(id));
    }

    /** Point the theme's product form at the resolved variant.
     *
     *  `[name="id"]`, not `input[name="id"]`: on two of the four storefronts it is a `<select>` listing
     *  every variant, not a hidden input. An input-only query found nothing there, so the shopper's
     *  housing choice never reached the form and the default variant was what got bought — no error, no
     *  sign anything was wrong. A select also needs the option to be SELECTED rather than its value
     *  poked, and needs a change event, because the theme's own script draws the price and availability
     *  from that select and will not notice a silent assignment. */
    setFormVariant(v) {
      const id = v ? String(v.id) : "";
      for (const field of this.variantFields()) this.writeVariantField(field, id);
      // the theme's own path first; only if it has none do we write its header ourselves
      const moved = this.writeOptionSelects(v);
      if (!moved) this.writeVariantFacts(v);
      this.showSku(v);
      /* A theme that handled the change rewrites the URL itself, and Galleria's keeps only `?variant=`
         — dropping the `?housing=` the picker had just put there. Harmless to the page in front of
         them, costly to the link they send a friend: it would arrive naming the variant but not the
         housing, so the picker would ask them to choose a housing the page is already showing.
         Re-asserted after, because the theme's handler runs after ours. */
      if (moved && this.chosenListing) this.writeUrl(this.chosenListing);
    }

    /** Also move the theme's OPTION selects, because that is what it draws the header from.
     *
     *  Setting `[name="id"]` makes the form post the right variant — the cart was always correct — but
     *  on Fashionopolism the price and SKU beside the title are rendered from the option selects
     *  (`product-select-…-option-N`), and nothing recomputes them from a variant id. So the shopper saw
     *  the FIRST variant's price above the picker's own: $21.00 in the header against $8.00 in the card
     *  and $8.00 in the cart, on a page where both numbers are real prices of two different gears.
     *
     *  Driving the option selects is the path the native dropdown takes, and the theme does the rest
     *  itself — price, SKU, gallery, and the ?variant= in the URL. Every value is checked before it is
     *  written: a theme that renders no option selects, or one whose options do not include this
     *  variant's, is left exactly as it was. */
    writeOptionSelects(v) {
      const opts = v && Array.isArray(v.options) ? v.options : null;
      if (!opts || !opts.length) return 0;
      /* Three namings across the four storefronts, and the third of them is why this was missed on the
         second pass: Fashionopolism writes `product-select-…-option-1`, Dawn `data-index="option1"`,
         and Galleria `SingleOptionSelector-1` — which matches neither. */
      const selects = [
        ...(this.form || document).querySelectorAll(
          "select[id*='-option-'], select[id^='SingleOptionSelector'], select[data-index^='option']",
        ),
      ];
      if (!selects.length) return 0;
      let moved = 0;
      selects.forEach((sel, i) => {
        // the select's own index when the theme marks it, else its position among them
        const m =
          /option-(\d+)/.exec(sel.id || "") ||
          /SingleOptionSelector-(\d+)/.exec(sel.id || "") ||
          /option(\d+)/.exec(sel.dataset.index || "");
        let idx = m ? Number(m[1]) : i;
        if (sel.dataset.index) idx -= 1; // Dawn counts from one, the others from zero
        const want = opts[idx];
        if (want == null || sel.value === want) return;
        if (![...sel.options].some((o) => o.value === want)) return;
        sel.value = want;
        sel.dispatchEvent(new Event("change", { bubbles: true }));
        moved++;
      });
      return moved;
    }

    /** No housing chosen yet means no SKU to show — so show none.
     *
     *  These listings carry a "Please select your housing" variant so the shopper has to choose, and its
     *  SKU is a placeholder: `SFORLPNONE`. The theme renders that beside the title like any other, so an
     *  o-ring page greets everyone with a part number that is not a part. (Its price is honest — all 52
     *  o-ring variants are $21 — which is why this showed up as a wrong SKU and nothing else.)
     *
     *  Hidden rather than blanked, so no stray "SKU:" label is left hanging, and restored the moment a
     *  housing is picked. */
    showSku(v) {
      for (const el of document.querySelectorAll(".variant-sku, .ProductMeta__SkuNumber, .product__sku, [data-product-sku]")) {
        const row = el.closest(".ProductMeta__Sku") || el;
        row.hidden = !v;
      }
    }

    /** Say what the theme cannot, when it has no selector to be driven by.
     *
     *  Prestige renders no variant control at all on these listings — the picker IS the control — so
     *  there is nothing to change and nothing recomputes the SKU and price beside the title. They stay
     *  on whatever the server rendered, which is the first variant's. On a range where every gear costs
     *  the same that shows only as a wrong SKU, which is how it went unnoticed; where the prices differ
     *  it is the $21-against-$8 contradiction again.
     *
     *  Written narrowly and by known name: an element that is not there is skipped, a label like
     *  "SKU:" is kept, and the money is formatted by the same shop format the picker uses for its own
     *  card, so the two cannot disagree about what a price looks like. */
    writeVariantFacts(v) {
      if (!v) return;
      const sku = String(v.sku || "").trim();
      if (sku) {
        for (const el of document.querySelectorAll(".variant-sku, .ProductMeta__SkuNumber, .product__sku, [data-product-sku]")) {
          const label = /^\s*(SKU\s*:?\s*)/i.exec(el.textContent || "");
          el.textContent = `${label ? label[1] : ""}${sku}`;
        }
      }
      // the sale price, then the struck-through one beside it when the theme shows both
      const now = document.querySelector(".ProductMeta__Price.Price--highlight, .ProductMeta__Price:not(.Price--compareAt)");
      if (now) setMoney(now, v.price, this.fmt);
      const was = document.querySelector(".ProductMeta__Price.Price--compareAt");
      if (was) {
        const higher = v.compare_at_price && v.compare_at_price > v.price;
        was.hidden = !higher;
        if (higher) setMoney(was, v.compare_at_price, this.fmt);
      }
    }

    /** Every variant field that belongs to this product, across all of the page's cart forms.
     *
     *  A product page can carry more than one: the buy form, Shopify's payment-terms form, and on some
     *  themes a second (sticky or mobile) copy. Whichever one the shopper ends up submitting has to
     *  hold the housing they chose, so they all get it. A form is this product's when it is the one
     *  holding the picker, or when one of its variant fields already names a variant of this listing —
     *  which leaves a recommendation elsewhere on the page alone. */
    variantFields() {
      const ids = this.variantIds();
      const out = [];
      for (const f of document.querySelectorAll('form[action*="/cart/add"]')) {
        const fields = [...f.querySelectorAll('[name="id"]')];
        const ours = f === this.form || fields.some((x) => ids.has(String(x.value)));
        if (ours) out.push(...fields);
      }
      if (this.form && !out.length) out.push(...this.form.querySelectorAll('[name="id"]'));
      return out;
    }

    /** Set one field, and tell the theme — a select needs its option selected, not its value poked. */
    writeVariantField(field, id) {
      if (field.tagName === "SELECT") {
        const opt = [...field.options].find((o) => o.value === id);
        if (!opt) return; // a variant the theme did not render (sold out and hidden): leave it alone
        field.value = id;
      } else {
        field.value = id;
      }
      // let the theme react — price, availability, its own gallery switching
      field.dispatchEvent(new Event("change", { bubbles: true }));
    }

    /** Choosing a housing is REQUIRED, and `disabled` on one button does not make it so.
     *
     *  This listing's variants are one gear per housing at ONE price, often sharing a SKU — so an
     *  ungated purchase ships a part that does not fit and nothing on the page contradicts it. Three
     *  routes have to be closed, not one:
     *    * the Add to cart button — `disabled` covers it;
     *    * the DYNAMIC CHECKOUT buttons (Shop Pay, PayPal), which submit the form themselves and never
     *      touch that button, so they would sell the default variant outright;
     *    * a programmatic `form.submit()`, which fires no submit event at all — the same trap the cart
     *      terms gate hit — so the click is caught in the capture phase, before the theme sees it.
     *  The listener sits on the buttons' own container rather than the form, so the quantity stepper
     *  and anything else in the form keep working. */
    gate(btn, ok) {
      const zone = btn.closest(".product-form__buttons") || btn.parentNode;
      if (zone) {
        zone.classList.toggle("dvl-g-ungated", !ok);
        zone.setAttribute("aria-disabled", String(!ok));
      }
      if (this.gated || !this.form) return;
      this.gated = true;
      const block = (e) => {
        if (this.chosen) return;
        e.preventDefault();
        e.stopPropagation();
        if (e.stopImmediatePropagation) e.stopImmediatePropagation();
        this.nag();
      };
      if (zone) zone.addEventListener("click", block, true);
      this.form.addEventListener("submit", block, true);
    }

    /** Say why nothing happened, and put the cursor where the answer goes. */
    nag() {
      // how often the required step is reached the hard way — the direct read on whether it is clear
      track("dvl_gate_hit", { surface: "picker" });
      this.hint.hidden = false;
      /* The listing names itself: "zoom gear" and "focus gear" read as ordinary words, "O-Ring Set"
         keeps its capitals and takes "an" - and an o-ring is not cut, it is sized. */
      const kind = this.gear.t || "part";
      const isGear = /gear/i.test(kind);
      const name = isGear ? kind.toLowerCase() : kind;
      const article = /^[aeiou]/i.test(name) ? "an" : "a";
      const how = isGear ? "is cut for one housing" : "is matched to one housing";
      this.hint.textContent = `Choose the housing you own first — ${article} ${name} ${how}, so we cannot pick for you.`;
      this.hint.setAttribute("role", "alert");
      this.combo.input.setAttribute("aria-invalid", "true");
      this.step2.dataset.invalid = "1";
      // nothing here may throw: nag() runs inside the capture-phase listener that holds the gate
      try {
        if (typeof this.step2.scrollIntoView === "function") {
          this.step2.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        this.combo.input.focus();
      } catch {
        /* an old engine, or a detached node — the gate itself has already done its job */
      }
    }
  }

  const boot = () => document.querySelectorAll("[data-dvl-gear-picker]").forEach((n) => new GearPicker(n));
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
  else boot();
})();
